import { memo, useDeferredValue, useMemo, useState } from "react"
import { Search, X, Star, ArrowLeft } from 'lucide-react'
import { useStore } from '../store'
import { StatusDot } from '../components/StatusDot'
import { OfflineBanner } from '../components/OfflineBanner'
import type { Destination, Source } from '@ultrix/shared'

interface Props {
  onSendTake: (destination: number, source: number) => Promise<{ ok: boolean; error?: string }>
  onLogout: () => void
}

type PhoneStep = 'dest' | 'source'
type FlashState = { source: number; ok: boolean }
type Xpoint = { source: number }

const desktopGrid = 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2'
const phoneGrid = 'grid grid-cols-2 sm:grid-cols-3 gap-2'

export function Home({ onSendTake, onLogout }: Props) {
  const { user, sources, destinations, state, favorites, bridgeOnline, lastSeen } = useStore()

  const [selectedDest, setSelectedDest] = useState<Destination | null>(null)
  const [destSearch, setDestSearch] = useState('')
  const [sourceSearch, setSourceSearch] = useState('')
  const deferredDestSearch = useDeferredValue(destSearch)
  const deferredSourceSearch = useDeferredValue(sourceSearch)
  const [destFavOnly, setDestFavOnly] = useState(false)
  const [sourceFavOnly, setSourceFavOnly] = useState(false)
  const [phoneStep, setPhoneStep] = useState<PhoneStep>('dest')
  const [flashingSource, setFlashingSource] = useState<FlashState | null>(null)
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null)

  const sourcesByNumber = useMemo(() => new Map(sources.map((s) => [s.number, s])), [sources])

  const filteredDests = useMemo(() => {
    const q = deferredDestSearch.trim().toLowerCase()
    const favSet = new Set(favorites.destinations)
    return destinations.filter((d) => {
      if (destFavOnly && !favSet.has(d.number)) return false
      if (!q) return true
      return d.name.toLowerCase().includes(q) || String(d.number).includes(q)
    })
  }, [destinations, deferredDestSearch, destFavOnly, favorites.destinations])

  const filteredSources = useMemo(() => {
    const q = deferredSourceSearch.trim().toLowerCase()
    const favSet = new Set(favorites.sources)
    return sources.filter((s) => {
      if (sourceFavOnly && !favSet.has(s.number)) return false
      if (!q) return true
      return s.name.toLowerCase().includes(q) || String(s.number).includes(q)
    })
  }, [sources, deferredSourceSearch, sourceFavOnly, favorites.sources])

  function pickDest(d: Destination) {
    setSelectedDest(d)
    setPhoneStep('source')
  }

  async function handleSourceClick(s: Source) {
    if (!selectedDest) {
      setFeedback({ kind: 'err', msg: 'Pick a destination first' })
      setTimeout(() => setFeedback(null), 2500)
      return
    }
    const destName = selectedDest.name
    const destNum = selectedDest.number
    const r = await onSendTake(destNum, s.number)
    if (r.ok) {
      setFlashingSource({ source: s.number, ok: true })
      setFeedback({ kind: 'ok', msg: `Routed ${s.name} → ${destName}` })
    } else {
      setFlashingSource({ source: s.number, ok: false })
      setFeedback({ kind: 'err', msg: `Failed: ${r.error ?? 'take failed'}` })
    }
    setTimeout(() => setFlashingSource(null), 600)
    setTimeout(() => setFeedback(null), 2500)
  }

  return (
    <div className="h-screen flex flex-col bg-bg text-ink">
      {/* ===== Top bar ===== */}
      <header className="px-4 py-3 border-b border-border flex items-center gap-3 shrink-0 bg-panel/60">
        <div className="font-semibold tracking-wide">ULTRIX</div>
        <div className="flex-1 text-sm text-sub truncate">{user?.username ?? ''}</div>
        <StatusDot online={bridgeOnline} />
        <button onClick={onLogout} className="text-sub hover:text-ink text-xs underline px-2 py-1">Sign out</button>
      </header>

      {!bridgeOnline && <OfflineBanner lastSeen={lastSeen} />}

      {feedback && (
        <div
          className={`px-4 py-2 text-sm border-b border-border flex items-center gap-2 shrink-0 ${
            feedback.kind === 'ok' ? 'bg-ok/15 text-ok' : 'bg-err/15 text-err'
          }`}
        >
          <span className="font-mono">{feedback.kind === 'ok' ? '✓' : '✗'}</span>
          <span className="truncate">{feedback.msg}</span>
        </div>
      )}

      {/* ===== Desktop / tablet: two-panel side-by-side ===== */}
      <div className="hidden md:flex flex-1 min-h-0">
        <section className="flex-1 min-w-0 border-r border-border flex flex-col min-h-0">
          <div className="px-3 pt-3 pb-0 text-xs uppercase tracking-wider text-sub shrink-0">Destinations</div>
          <SearchHeader
            value={destSearch}
            onChange={setDestSearch}
            placeholder="Search destinations…"
            favOnly={destFavOnly}
            onToggleFav={() => setDestFavOnly(!destFavOnly)}
            shownCount={filteredDests.length}
            totalCount={destinations.length}
          />
          <DestGrid
            items={filteredDests}
            selectedNumber={selectedDest?.number}
            state={state}
            sourcesByNumber={sourcesByNumber}
            gridClass={desktopGrid}
            onPick={setSelectedDest}
          />
        </section>
        <section className="flex-1 min-w-0 flex flex-col min-h-0">
          <div className="px-3 pt-3 pb-0 text-xs uppercase tracking-wider text-sub shrink-0 flex items-center gap-2">
            <span>Sources</span>
            {!selectedDest && <span className="text-sub/80 normal-case tracking-normal italic">· pick a destination first</span>}
            {selectedDest && (
              <span className="text-sub normal-case tracking-normal">
                · routing to <span className="text-ink font-medium">{selectedDest.name}</span>
              </span>
            )}
          </div>
          <SearchHeader
            value={sourceSearch}
            onChange={setSourceSearch}
            placeholder="Search sources…"
            favOnly={sourceFavOnly}
            onToggleFav={() => setSourceFavOnly(!sourceFavOnly)}
            shownCount={filteredSources.length}
            totalCount={sources.length}
          />
          <SourceGrid
            items={filteredSources}
            flashing={flashingSource}
            disabled={!selectedDest || !bridgeOnline}
            gridClass={desktopGrid}
            onPick={handleSourceClick}
          />
        </section>
      </div>

      {/* ===== Phone: two-step flow ===== */}
      <div className="md:hidden flex-1 flex flex-col min-h-0">
        {phoneStep === 'dest' && (
          <section className="flex-1 flex flex-col min-h-0">
            <div className="px-3 pt-3 text-xs uppercase tracking-wider text-sub shrink-0">Destinations</div>
            <SearchHeader
              value={destSearch}
              onChange={setDestSearch}
              placeholder="Search destinations…"
              favOnly={destFavOnly}
              onToggleFav={() => setDestFavOnly(!destFavOnly)}
              shownCount={filteredDests.length}
              totalCount={destinations.length}
            />
            <DestGrid
              items={filteredDests}
              selectedNumber={selectedDest?.number}
              state={state}
              sourcesByNumber={sourcesByNumber}
              gridClass={phoneGrid}
              onPick={pickDest}
            />
          </section>
        )}

        {phoneStep === 'source' && selectedDest && (
          <section className="flex-1 flex flex-col min-h-0">
            <div className="px-3 py-2 border-b border-border flex items-center gap-3 shrink-0 bg-panel/40">
              <button
                onClick={() => setPhoneStep('dest')}
                className="p-1.5 rounded-md border border-border text-sub hover:text-ink hover:border-sub"
                aria-label="Back"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-sub">Routing to</div>
                <div className="truncate font-medium">{selectedDest.name}</div>
              </div>
            </div>
            <div className="px-3 pt-3 text-xs uppercase tracking-wider text-sub shrink-0">Sources</div>
            <SearchHeader
              value={sourceSearch}
              onChange={setSourceSearch}
              placeholder="Search sources…"
              favOnly={sourceFavOnly}
              onToggleFav={() => setSourceFavOnly(!sourceFavOnly)}
              shownCount={filteredSources.length}
              totalCount={sources.length}
            />
            <SourceGrid
              items={filteredSources}
              flashing={flashingSource}
              disabled={!bridgeOnline}
              gridClass={phoneGrid}
              onPick={handleSourceClick}
            />
          </section>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Module-level subcomponents
// ----------------------------------------------------------------------------
// Keeping these OUT of the Home function is critical for input snappiness:
// if they were defined inside Home, React would treat them as a new component
// type on every Home re-render (every keystroke) and unmount/remount their
// entire subtree, defeating React.memo on the grid cells.
// ============================================================================

interface SearchHeaderProps {
  value: string
  onChange: (v: string) => void
  placeholder: string
  favOnly: boolean
  onToggleFav: () => void
  shownCount: number
  totalCount: number
}

function SearchHeader({ value, onChange, placeholder, favOnly, onToggleFav, shownCount, totalCount }: SearchHeaderProps) {
  return (
    <div className="p-3 border-b border-border flex items-center gap-2 shrink-0 bg-panel/50">
      <SearchBox value={value} onChange={onChange} placeholder={placeholder} />
      <FavToggle active={favOnly} onClick={onToggleFav} />
      <div className="text-xs text-sub shrink-0 tabular-nums w-12 text-right">{shownCount}/{totalCount}</div>
    </div>
  )
}

interface DestGridProps {
  items: Destination[]
  selectedNumber: number | undefined
  state: Map<number, Xpoint>
  sourcesByNumber: Map<number, Source>
  gridClass: string
  onPick: (d: Destination) => void
}

function DestGrid({ items, selectedNumber, state, sourcesByNumber, gridClass, onPick }: DestGridProps) {
  return (
    <div className="flex-1 overflow-y-auto p-2">
      <div className={gridClass}>
        {items.map((d) => {
          const xp = state.get(d.number)
          const srcName = xp ? (sourcesByNumber.get(xp.source)?.name ?? `#${xp.source}`) : null
          return (
            <DestCell
              key={d.number}
              dest={d}
              isSelected={selectedNumber === d.number}
              srcName={srcName}
              onPick={onPick}
            />
          )
        })}
      </div>
      {items.length === 0 && (
        <div className="text-xs text-sub italic px-2 py-6 text-center">No matches</div>
      )}
    </div>
  )
}

interface SourceGridProps {
  items: Source[]
  flashing: FlashState | null
  disabled: boolean
  gridClass: string
  onPick: (s: Source) => void
}

function SourceGrid({ items, flashing, disabled, gridClass, onPick }: SourceGridProps) {
  return (
    <div className="flex-1 overflow-y-auto p-2">
      <div className={gridClass}>
        {items.map((src) => {
          const isFlashing = flashing?.source === src.number
          const flashState: 'ok' | 'err' | null = isFlashing ? (flashing?.ok ? 'ok' : 'err') : null
          return (
            <SourceCell
              key={src.number}
              source={src}
              flashState={flashState}
              disabled={disabled}
              onPick={onPick}
            />
          )
        })}
      </div>
      {items.length === 0 && (
        <div className="text-xs text-sub italic px-2 py-6 text-center">No matches</div>
      )}
    </div>
  )
}

// ===== Small shared leaf components =====

function SearchBox({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  placeholder: string
}) {
  return (
    <div className="relative flex-1 min-w-0">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sub pointer-events-none" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-bg border border-border rounded-md pl-9 pr-8 py-1.5 text-sm focus:outline-none focus:border-accent"
      />
      {value && (
        <button
          onClick={() => onChange('')}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-sub hover:text-ink"
          aria-label="Clear"
          type="button"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  )
}

function FavToggle({ active, onClick }: { active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      type="button"
      className={`p-1.5 rounded-md border shrink-0 ${
        active
          ? 'bg-accent border-accent text-white'
          : 'bg-panel border-border text-sub hover:text-ink'
      }`}
      title={active ? 'Show all' : 'Show favorites only'}
      aria-label="Filter favorites"
      aria-pressed={active}
    >
      <Star className={`w-4 h-4 ${active ? 'fill-current' : ''}`} />
    </button>
  )
}

// ===== Memoized grid cells =====
// Only re-render when their props change so typing into the search doesn't
// redo work for every unchanged button.

interface DestCellProps {
  dest: Destination
  isSelected: boolean
  srcName: string | null
  onPick: (d: Destination) => void
}
const DestCell = memo(function DestCell({ dest, isSelected, srcName, onPick }: DestCellProps) {
  return (
    <button
      onClick={() => onPick(dest)}
      className={`p-2 rounded-md text-left text-sm border transition-colors ${
        isSelected
          ? 'bg-accent border-accent text-white'
          : 'bg-panel border-border text-ink hover:border-sub'
      }`}
    >
      <div className="font-medium truncate leading-tight">{dest.name}</div>
      <div className={`text-xs truncate ${isSelected ? 'text-white/70' : 'text-sub'}`}>#{dest.number}</div>
      <div className="text-xs truncate mt-0.5">
        {srcName ? (
          <span className="text-ok">{srcName}</span>
        ) : (
          <span className={`italic ${isSelected ? 'text-white/60' : 'text-sub'}`}>—</span>
        )}
      </div>
    </button>
  )
})

interface SourceCellProps {
  source: Source
  flashState: 'ok' | 'err' | null
  disabled: boolean
  onPick: (s: Source) => void
}
const SourceCell = memo(function SourceCell({ source, flashState, disabled, onPick }: SourceCellProps) {
  const classes =
    flashState === 'ok'
      ? 'bg-ok/30 border-ok text-ink'
      : flashState === 'err'
      ? 'bg-err/30 border-err text-ink'
      : 'bg-panel border-border text-ink hover:border-sub'
  return (
    <button
      onClick={() => onPick(source)}
      disabled={disabled}
      className={`p-2 rounded-md text-left text-sm border transition-colors ${classes} ${
        disabled ? 'opacity-50 cursor-not-allowed hover:border-border' : ''
      }`}
    >
      <div className="font-medium truncate leading-tight">{source.name}</div>
      <div className="text-xs text-sub truncate">#{source.number}</div>
    </button>
  )
})
